/**
 * Conversion of a rendered canvas into the 1-bit image a thermal printer actually prints.
 *
 * Thermal heads print black or nothing. If we sent anti-aliased grey pixels, the terminal
 * would threshold them for us and thin strokes would disappear. We render at a higher
 * resolution and reduce to paper resolution ourselves, so we control what becomes ink.
 *
 * Text is thresholded: a paper dot is black when enough of it is covered by ink. Logos and
 * other continuous-tone regions are dithered instead, so shading survives as a dot pattern.
 */

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Fraction of a paper dot that must be covered by ink for it to print black.
 * Lower keeps thin strokes; higher keeps glyphs from bleeding together.
 */
export const INK_THRESHOLD = 0.35;

/** 4x4 Bayer matrix, normalised to 0..1 thresholds, for ordered dithering of images. */
const BAYER_4X4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5],
].map((row) => row.map((v) => (v + 0.5) / 16));

const insideAny = (x: number, y: number, rects: Rect[]) =>
  rects.some((r) => x >= r.x && x < r.x + r.width && y >= r.y && y < r.y + r.height);

export interface BinariseOptions {
  /** The supersampled source. */
  source: ImageData;
  /** How many source pixels per paper dot on each axis. */
  supersample: number;
  /** Regions (in paper dots) to dither rather than threshold. */
  ditherRects?: Rect[];
  /** Target ImageData to fill, sized (source.width / supersample) x (source.height / supersample). */
  target: ImageData;
}

/** Reduces a supersampled RGBA canvas to a pure black-and-white image at paper resolution. */
export const binarise = ({ source, supersample, ditherRects = [], target }: BinariseOptions) => {
  const { width, height } = target;
  const src = source.data;
  const out = target.data;
  const samples = supersample * supersample;
  const srcWidth = source.width;

  for (let py = 0; py < height; py++) {
    const dither = ditherRects.length > 0;

    for (let px = 0; px < width; px++) {
      // Average darkness (0 = white, 1 = black) over the block of source pixels.
      let ink = 0;

      for (let sy = 0; sy < supersample; sy++) {
        const rowStart = (py * supersample + sy) * srcWidth;

        for (let sx = 0; sx < supersample; sx++) {
          const idx = (rowStart + px * supersample + sx) * 4;
          // Luminance from RGB; alpha treated as coverage over white.
          const alpha = src[idx + 3] / 255;
          const lum = (src[idx] * 0.299 + src[idx + 1] * 0.587 + src[idx + 2] * 0.114) / 255;
          ink += (1 - lum) * alpha;
        }
      }

      const darkness = ink / samples;

      const threshold =
        dither && insideAny(px, py, ditherRects) ? BAYER_4X4[py % 4][px % 4] : INK_THRESHOLD;

      const value = darkness >= threshold ? 0 : 255;
      const o = (py * width + px) * 4;

      out[o] = value;
      out[o + 1] = value;
      out[o + 2] = value;
      out[o + 3] = 255;
    }
  }

  return target;
};
